/**
 * Lightweight structural validation for Freemarker templates.
 *
 * This is NOT a full Freemarker parser — it is a pragmatic checker for the
 * mistakes authors actually make while editing an email/notification body:
 *
 *  1. An interpolation opened with `${` (or `#{`) that is never closed with `}`.
 *  2. A block directive (`<#if>`, `<#list>`, `<@macro>`, …) that is never closed,
 *     closed with no matching open, or closed by the wrong tag.
 *  3. An FTL comment `<#-- … -->` that is never closed.
 *
 * It deliberately ignores standalone directives (`<#assign>`, `<#include>`,
 * `<#else>`, …) and self-closing tags (`<@foo/>`) because those need no closing
 * tag. Expressions containing a literal `>` inside a directive tag are a known
 * limitation and are not diagnosed.
 */

export interface FreemarkerProblem {
  /** Human-readable, ready to show in the validation panel. */
  message: string;
  /** The offending token/tag, when one can be pinpointed. */
  snippet?: string;
}

/**
 * Block directives that MUST be closed with a matching `</#name>`. Everything
 * else beginning with `#` is treated as standalone (needs no close): `assign`,
 * `global`, `local`, `include`, `import`, `return`, `break`, `continue`,
 * `stop`, `else`, `elseif`, `case`, `default`, `items`, `sep`, `nested`,
 * `recover`, `setting`, `ftl`, `t`/`lt`/`rt`/`nt`, `flush`, `fallback`, …
 */
const BLOCK_DIRECTIVES = new Set([
  'if',
  'list',
  'switch',
  'macro',
  'function',
  'attempt',
  'compress',
  'escape',
  'noescape',
  'outputformat',
  'autoesc',
  'noautoesc',
]);

interface OpenTag {
  kind: '#' | '@';
  name: string;
}

/** All FTL/interpolation opener + directive-tag tokens, in source order. */
const TAG_PATTERN = /<(\/?)([#@])([a-zA-Z][a-zA-Z0-9_.]*)?([^>]*)>/g;

/** Reports every unclosed FTL comment (`<#-- …` with no `-->`). */
function commentProblems(source: string): FreemarkerProblem[] {
  const problems: FreemarkerProblem[] = [];
  let cursor = 0;
  for (;;) {
    const open = source.indexOf('<#--', cursor);
    if (open === -1) break;
    const close = source.indexOf('-->', open + 4);
    if (close === -1) {
      problems.push({
        message: 'Unclosed Freemarker comment — add a closing `-->`.',
        snippet: source.slice(open, Math.min(open + 24, source.length)),
      });
      break;
    }
    cursor = close + 3;
  }
  return problems;
}

/** Remove complete FTL comments so their contents are never mis-diagnosed. */
function stripComments(source: string): string {
  return source.replace(/<#--[\s\S]*?-->/g, '');
}

/** Reports every interpolation (`${…}` / `#{…}`) that is never closed. */
function interpolationProblems(source: string): FreemarkerProblem[] {
  const problems: FreemarkerProblem[] = [];
  const opener = /[$#]\{/g;
  let match: RegExpExecArray | null;
  while ((match = opener.exec(source))) {
    const start = match.index;
    // Walk forward honouring nested `{ … }` so `${a{b}}` closes correctly.
    let depth = 1;
    let i = start + 2;
    for (; i < source.length && depth > 0; i++) {
      if (source[i] === '{') depth++;
      else if (source[i] === '}') depth--;
    }
    if (depth > 0) {
      problems.push({
        message: 'Unclosed Freemarker expression — add a closing `}`.',
        snippet: source
          .slice(start, Math.min(start + 24, source.length))
          .trim(),
      });
    }
  }
  return problems;
}

/** Reports unbalanced / mismatched block directives and macro calls. */
function directiveProblems(source: string): FreemarkerProblem[] {
  const problems: FreemarkerProblem[] = [];
  // Ignore tokens inside comments so a directive in a comment is not counted.
  const scanned = stripComments(source);
  const stack: OpenTag[] = [];
  let match: RegExpExecArray | null;
  TAG_PATTERN.lastIndex = 0;
  while ((match = TAG_PATTERN.exec(scanned))) {
    const isClose = match[1] === '/';
    const kind = match[2] as '#' | '@';
    const name = (match[3] || '').toLowerCase();
    const rest = match[4] || '';
    const selfClosing = /\/\s*$/.test(rest);

    if (kind === '#' && !BLOCK_DIRECTIVES.has(name)) continue; // standalone
    if (!isClose && selfClosing) continue; // `<@foo/>` needs no close

    if (isClose) {
      const top = stack.pop();
      const label = kind === '@' ? `</@${name}>` : `</#${name}>`;
      if (!top) {
        problems.push({
          message: `Unexpected closing \`${label}\` — it has no matching opening tag.`,
          snippet: label,
        });
        continue;
      }
      const matches =
        top.kind === kind && (kind === '@' ? true : top.name === name);
      if (!matches) {
        const openLabel =
          top.kind === '@' ? `<@${top.name}>` : `<#${top.name}>`;
        problems.push({
          message: `Mismatched Freemarker tags — \`${openLabel}\` is closed by \`${label}\`.`,
          snippet: label,
        });
      }
    } else {
      stack.push({ kind, name });
    }
  }

  for (const open of stack) {
    const openLabel = open.kind === '@' ? `<@${open.name}>` : `<#${open.name}>`;
    const closeLabel =
      open.kind === '@' ? `</@${open.name}>` : `</#${open.name}>`;
    problems.push({
      message: `Unclosed \`${openLabel}\` — add a matching \`${closeLabel}\`.`,
      snippet: openLabel,
    });
  }

  return problems;
}

/**
 * Validate a Freemarker template's structure. Returns an ordered list of
 * problems (comments first, then expressions, then directives); an empty array
 * means no structural issue was found.
 */
export function validateFreemarker(source: string): FreemarkerProblem[] {
  const text = source || '';
  // Comment detection needs the raw text (an unclosed `<#--` has no `-->`);
  // everything else scans the comment-stripped text so a `${`/directive that
  // lives inside a comment is never mis-flagged.
  const withoutComments = stripComments(text);
  return [
    ...commentProblems(text),
    ...interpolationProblems(withoutComments),
    ...directiveProblems(withoutComments),
  ];
}
